import { useEffect, useRef, useState } from 'react';
import { imagePdfAndDocExtensions } from '@common/utils/fileExtensions';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import type { IPatientContactInfoDraft } from '../../../../../../common/validation/patientContactInfo';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider } from '../../../../elements/common';
import { ContactWithRequesterOptionsAddEdit } from '../../../../elements/ContactWithRequesterOptionsAddEdit';
import { DiagnosisQuestionsAddEdit } from '../../../../elements/DiagnosisQuestionsAddEdit';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { addFormAnalytics, addFormConversion, addTrackEvent, addTrackFormSubmit } from '../../utils/formAnalytics';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceConsumerSupervisorSelector } from '../../utils/v1/ServiceConsumerSupervisorSelector';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { Ggzcf2fV1FollowingSteps } from './Ggzcf2fV1FollowingSteps';

interface ICustomControlDataGgzcf2f {
    conclusie_diagnose: ConclusieDiagnoseOption | null;
    contactgegevens_patient: IPatientContactInfoDraft;
    contactWithRequesterOptions: string | null;
    toelichting: string | null;
    vraag: string | null;
}

export function Ggzcf2fV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form
                formRef={formRef}
                id="Ggzcf2fV1RequestAddEditId"
                name="Ggzcf2fV1RequestAddEdit"
                onSubmit={async () => null}
            >
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedDiagnosticReportProviderOrganizationId}
                    initialPractitionerId={props.request.selectedDiagnosticReportProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        await props.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedDiagnosticReportProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                    searchInNetwork
                    serviceProviderServiceId={props.service.id}
                    serviceType={props.service.serviceType}
                />

                <ServiceConsumerSupervisorSelector {...props} />

                <FormDivider />

                <HeaderDivider text={translateKey('procedure.procedure')} />

                <AssetDropzoneWithActions
                    acceptedFiles={imagePdfAndDocExtensions}
                    assets={props.request.assets}
                    assetsBaseUrl={FilesUrlBase.requests}
                    cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                    onChange={async (assets) => await props.handleChange('assets', assets)}
                    onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                    processActivityId={props.request.id}
                    service={props.service}
                    showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                    updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                />

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                <DiagnosisQuestionsAddEdit {...props} />

                <PatientContactAddEdit {...props} />

                <ContactWithRequesterOptionsAddEdit {...props} />
                <Ggzcf2fV1FollowingSteps />
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            revertChanges={props.revertRequestChanges}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,

                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        addTrackFormSubmit(saveSendButtonRef.current);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        if (saveType === 'final' && props.request.customControlData.contactWithRequesterOptions) {
            addTrackEvent('Ggzcf2fV1', 'filled', 'ContactWithRequesterOptions');
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            const result = await getJson(
                `/api/organizations/${props.request.selectedDiagnosticReportProviderOrganizationId}`
            );
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.consultRequestSubmit', result.json.name)
            );
            addFormConversion(formRef.current);
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await props.onSave(requestId);

        if (props.reloadProcess) {
            await props.reloadProcess(ReloadEpisode.reloadAll);
        }

        if (props.reloadPatient) {
            await props.reloadPatient();
        }
    }
}

export function ggzcf2fV1InitializeCustomControlData(): ICustomControlDataGgzcf2f {
    return {
        conclusie_diagnose: null,
        vraag: null,
        toelichting: null,
        contactWithRequesterOptions: null,
        contactgegevens_patient: {
            emailadres: null,
            mobileNumber: null,
            homePhoneNumber: null,
        },
    };
}
